import { Logger } from 'utilities';
import { INDEXED_DB_TIMEOUT } from './indexedDBStorageManager.constants';
import type {
  IndexedDBStorageManager,
  IndexedDBStorageManagerOptions,
  IndexedDBStorageRecord,
} from './indexedDBStorageManager.types';
import {
  getFallback,
  handleOpenBlocked,
  handleOpenError,
  handleOpenSuccess,
  handleUpgradeNeeded,
  idbRequest,
  removeFallback,
  setFallback,
} from './indexedDBStorageManager.utils';

export const createIndexedDBStorageManager = ({
  dbName,
  dbVersion,
  storeName,
  fallbackStorageKey,
  logNamespace,
}: IndexedDBStorageManagerOptions): IndexedDBStorageManager => {
  let dbPromise: Promise<IDBDatabase> | null = null;
  const memoryFallback = new Map<string, unknown>();

  const resetDbPromise = (): void => {
    dbPromise = null;
  };

  const openDB = (): Promise<IDBDatabase> => {
    if (dbPromise) {
      return dbPromise;
    }

    dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      if (typeof indexedDB === 'undefined') {
        resetDbPromise();
        reject(new Error('IndexedDB not available'));
        return;
      }

      const req = indexedDB.open(dbName, dbVersion);

      // If the open request neither resolves nor errors (browser bug, resource pressure,
      // or extension interference), reject so callers do not hang indefinitely.
      let timeoutId: ReturnType<typeof setTimeout>;
      const clearOpenTimeout = () => clearTimeout(timeoutId);

      timeoutId = setTimeout(() => {
        resetDbPromise();
        reject(new Error(`${logNamespace} IndexedDB open timed out`));
      }, INDEXED_DB_TIMEOUT);

      req.onupgradeneeded = event => handleUpgradeNeeded(event, storeName);
      req.onsuccess = event => handleOpenSuccess(event, resolve, clearOpenTimeout, resetDbPromise);
      req.onerror = () => handleOpenError(req.error, reject, clearOpenTimeout, resetDbPromise);
      req.onblocked = () => handleOpenBlocked(logNamespace);
    });

    return dbPromise;
  };

  const fallbackDeps = {
    memoryFallback,
    fallbackStorageKey,
    logNamespace,
  };

  return {
    get: async <T,>(key: string) => {
      try {
        const db = await openDB();
        const tx = db.transaction(storeName, 'readonly');
        const record = await idbRequest<IndexedDBStorageRecord<T> | undefined>(
          tx.objectStore(storeName).get(key)
        );
        return record?.value;
      } catch (error) {
        Logger.debug(`${logNamespace} IndexedDB read failed; using fallback:`, error);
        return getFallback<T>({ key, ...fallbackDeps });
      }
    },
    set: async (key: string, value: unknown) => {
      setFallback({ key, value, ...fallbackDeps });

      try {
        const db = await openDB();
        const tx = db.transaction(storeName, 'readwrite');
        await idbRequest(
          tx.objectStore(storeName).put({
            key,
            value,
            updatedAt: Date.now(),
          } satisfies IndexedDBStorageRecord)
        );
        removeFallback({ key, ...fallbackDeps });
      } catch (error) {
        Logger.debug(`${logNamespace} IndexedDB write failed; fallback remains active:`, error);
      }
    },
    remove: async (key: string) => {
      removeFallback({ key, ...fallbackDeps });

      try {
        const db = await openDB();
        const tx = db.transaction(storeName, 'readwrite');
        await idbRequest(tx.objectStore(storeName).delete(key));
      } catch (error) {
        Logger.debug(`${logNamespace} IndexedDB removal failed:`, error);
      }
    },
  };
};
